import { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import MainLayout from '../../components/layout/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { authenticatedFetch } from '../../api/apiClient';
import { API_ROUTES } from '../../shared/apiRoutes/apiRoutes';
import { ReadAloudTextButton } from '../Training/components/ReadAloudButton';
import {
  AGREEMENT_ADMIN_TYPES,
  canAccessAgreementsAdmin,
  currentPublishedVersion,
  formatAgreementDate,
  isTestAgreement,
  validateAgreementDraft,
  withCurrentFlags,
} from '../../utils/agreementAdmin';

const emptyDraft = {
  title: '',
  version: '',
  body: '',
};

function versionsUrl(agreementType) {
  return API_ROUTES.ADMIN_AGREEMENT_VERSIONS.replace(':agreementType', agreementType);
}

const AgreementsPage = () => {
  const { userClaims, userData } = useAuth();
  const role = userClaims?.role || userData?.role;
  const allowed = canAccessAgreementsAdmin(role);

  const [versionsByType, setVersionsByType] = useState({ nda: [], beta: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorType, setEditorType] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [draftErrors, setDraftErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [viewing, setViewing] = useState(null);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(
        AGREEMENT_ADMIN_TYPES.map(async ({ type }) => {
          const resp = await authenticatedFetch(versionsUrl(type));
          const json = await resp.json();
          if (resp.status === 403) {
            throw new Error('Admin access required');
          }
          if (!resp.ok) {
            throw new Error(json.error || `Failed to load ${type} versions`);
          }
          return [type, json.data || []];
        })
      );
      setVersionsByType(Object.fromEntries(results));
    } catch (err) {
      setError(err.message || 'Failed to load agreements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) loadVersions();
    else setLoading(false);
  }, [allowed, loadVersions]);

  const openEditor = (type, suggested) => {
    setEditorType(type);
    setDraft({
      title: suggested?.title || '',
      version: '',
      body: '',
    });
    setDraftErrors({});
    setShowPreview(false);
  };

  const closeEditor = () => {
    setEditorType(null);
    setDraft(emptyDraft);
    setDraftErrors({});
    setShowPreview(false);
  };

  const handlePublish = async () => {
    const result = validateAgreementDraft(draft);
    setDraftErrors(result.errors);
    if (!result.valid) return;
    setPublishing(true);
    try {
      const resp = await authenticatedFetch(versionsUrl(editorType), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: draft.title.trim(),
          version: draft.version.trim(),
          body: draft.body,
        }),
      });
      const json = await resp.json();
      if (resp.status === 403) {
        throw new Error('Admin access required');
      }
      if (!resp.ok) {
        throw new Error(json.error || 'Could not publish agreement');
      }
      toast.success('Agreement version published');
      closeEditor();
      await loadVersions();
    } catch (err) {
      toast.error(err.message || 'Publish failed');
      setDraftErrors((prev) => ({ ...prev, submit: err.message }));
    } finally {
      setPublishing(false);
    }
  };

  if (!allowed) {
    return (
      <MainLayout>
        <div className="p-6 max-w-4xl mx-auto bg-red-50 border border-red-200 rounded-md text-center">
          <h1 className="text-xl font-bold text-red-700 mb-2">Access Denied</h1>
          <p className="text-red-600">
            Platform agreements can only be managed by admins and superadmins.
          </p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Agreements</h1>
        <p className="text-sm text-gray-600 mt-1">
          Publish platform NDA and Beta Tester Agreement versions. Users accept the current
          published version. Published text cannot be edited.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">{error}</div>
      )}

      {loading ? (
        <LoadingSpinner text="Loading agreements..." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {AGREEMENT_ADMIN_TYPES.map(({ type, label }) => {
            const versions = withCurrentFlags(versionsByType[type] || []);
            const current = currentPublishedVersion(versionsByType[type] || []);
            return (
              <Card key={type} title={label}>
                <div className="p-4 space-y-4">
                  {current ? (
                    <div>
                      {isTestAgreement(current) && (
                        <div className="mb-3 p-2 text-sm bg-amber-50 border border-amber-300 text-amber-800 rounded">
                          ⚠ Test agreement currently published
                        </div>
                      )}
                      <dl className="text-sm text-gray-700 space-y-1">
                        <div>
                          <span className="text-gray-500">Current: </span>
                          <span className="font-medium">{current.version}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Title: </span>
                          <span>{current.title || '—'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Published: </span>
                          <span>{formatAgreementDate(current.publishedAt || current.createdAt)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Status: </span>
                          <span className="font-medium">{current.status || 'published'}</span>
                        </div>
                      </dl>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <Button type="button" variant="secondary" onClick={() => setViewing({ ...current, typeLabel: label })}>
                          View
                        </Button>
                        <Button type="button" onClick={() => openEditor(type, current)}>
                          Create New Version
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-700 mb-3">No published agreement</p>
                      <Button type="button" onClick={() => openEditor(type)}>
                        Create First Version
                      </Button>
                    </div>
                  )}

                  {versions.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-800 mb-2">Version history</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-gray-500">
                              <th className="py-1 pr-3 font-medium">Version</th>
                              <th className="py-1 pr-3 font-medium">Title</th>
                              <th className="py-1 pr-3 font-medium">Published</th>
                              <th className="py-1 pr-3 font-medium"> </th>
                              <th className="py-1 font-medium"> </th>
                            </tr>
                          </thead>
                          <tbody>
                            {versions.map((item) => (
                              <tr key={item.id} className="border-t border-gray-100">
                                <td className="py-1.5 pr-3">{item.version}</td>
                                <td className="py-1.5 pr-3">{item.title}</td>
                                <td className="py-1.5 pr-3 text-gray-600">
                                  {formatAgreementDate(item.publishedAt || item.createdAt)}
                                </td>
                                <td className="py-1.5 pr-3">
                                  <span className={item.isCurrent ? 'text-green-700 font-medium' : 'text-gray-500'}>
                                    {item.historyLabel}
                                  </span>
                                </td>
                                <td className="py-1.5">
                                  <button
                                    type="button"
                                    className="text-blue-600 hover:underline"
                                    onClick={() => setViewing({ ...item, typeLabel: label })}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start gap-4 mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{viewing.title}</h2>
                  <p className="text-sm text-gray-500">
                    {viewing.typeLabel} · Version {viewing.version} · {viewing.historyLabel || (viewing.isCurrent ? 'current' : 'previous')}
                  </p>
                </div>
                <button type="button" className="text-gray-500 hover:text-gray-700" onClick={() => setViewing(null)}>
                  Close
                </button>
              </div>
              {isTestAgreement(viewing) && (
                <div className="mb-3 p-2 text-sm bg-amber-50 border border-amber-300 text-amber-800 rounded">
                  {viewing.isCurrent
                    ? '⚠ Test agreement currently published'
                    : '⚠ This version is marked TEST / NOT LEGAL'}
                </div>
              )}
              <p className="text-xs text-gray-500 mb-1">
                Published {formatAgreementDate(viewing.publishedAt || viewing.createdAt)}
                {viewing.hashSha256 ? ` · Hash ${viewing.hashSha256}` : ''}
              </p>
              <ReadAloudTextButton
                text={[viewing.title, viewing.body].filter(Boolean).join('\n\n')}
                sx={{ mb: 1, px: 0, textTransform: 'none' }}
              >
                Read aloud
              </ReadAloudTextButton>
              <div className="prose prose-sm max-w-none bg-gray-50 border border-gray-200 rounded p-4">
                <ReactMarkdown>{viewing.body || ''}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      {editorType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Create New Version</h2>
              <p className="text-sm text-gray-500 mb-4">
                {editorType === 'nda' ? 'NDA' : 'Beta Tester Agreement'}. Publishing creates a new
                immutable version and makes it current.
              </p>
              <div className="space-y-3">
                <div>
                  <label htmlFor="agreement-title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    id="agreement-title"
                    value={draft.title}
                    onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                  />
                  {draftErrors.title && <p className="text-sm text-red-600 mt-1">{draftErrors.title}</p>}
                </div>
                <div>
                  <label htmlFor="agreement-version" className="block text-sm font-medium text-gray-700 mb-1">
                    Version
                  </label>
                  <input
                    id="agreement-version"
                    value={draft.version}
                    onChange={(e) => setDraft((prev) => ({ ...prev, version: e.target.value }))}
                    placeholder="e.g. 2 or 2026-09-01"
                    className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                  />
                  {draftErrors.version && <p className="text-sm text-red-600 mt-1">{draftErrors.version}</p>}
                </div>
                <div>
                  <label htmlFor="agreement-body" className="block text-sm font-medium text-gray-700 mb-1">
                    Agreement body
                  </label>
                  <textarea
                    id="agreement-body"
                    value={draft.body}
                    onChange={(e) => setDraft((prev) => ({ ...prev, body: e.target.value }))}
                    rows={10}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm font-mono"
                  />
                  {draftErrors.body && <p className="text-sm text-red-600 mt-1">{draftErrors.body}</p>}
                </div>
                {draftErrors.submit && <p className="text-sm text-red-600">{draftErrors.submit}</p>}
                {showPreview && (
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <h3 className="text-sm font-medium text-gray-700">Preview</h3>
                      <ReadAloudTextButton
                        text={[draft.title, draft.body].filter(Boolean).join('\n\n')}
                        sx={{ px: 0, textTransform: 'none' }}
                      >
                        Read aloud
                      </ReadAloudTextButton>
                    </div>
                    <div className="prose prose-sm max-w-none bg-gray-50 border border-gray-200 rounded p-4 max-h-64 overflow-y-auto">
                      <ReactMarkdown>{draft.body || ''}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2 mt-6">
                <Button type="button" variant="secondary" onClick={closeEditor} disabled={publishing}>
                  Cancel
                </Button>
                <Button type="button" variant="secondary" onClick={() => setShowPreview((v) => !v)}>
                  Preview
                </Button>
                <Button type="button" onClick={handlePublish} disabled={publishing}>
                  {publishing ? 'Publishing...' : 'Publish'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AgreementsPage;
